import apiClient, { getApiBaseUrl } from './apiClient';
import i18n from '../i18n/config';
import { useAuthStore } from '../stores/authStore';

export interface ImportValidationError {
  path: string;
  message: string;
}

export interface ImportValidationCounts {
  domains: number;
  usecases: number;
  cadrages: number;
}

export interface ImportValidationReport {
  ok: boolean;
  errors: ImportValidationError[];
  counts: ImportValidationCounts;
  organization_name?: string | null;
  sector_template_id?: string | null;
  parsed?: unknown;
}

export type ImportEventName = 'progress' | 'complete' | 'error';

export interface ImportEvent {
  event: ImportEventName;
  data: Record<string, unknown>;
}

export interface CommitResult {
  organizationId: string;
}

const apiBaseURL = '/organizations/import';

const buildAuthHeaders = (): Record<string, string> => {
  const state = useAuthStore.getState();
  const headers: Record<string, string> = {};
  if (state.accessToken) {
    headers['Authorization'] = `Bearer ${state.accessToken}`;
  }
  if (state.impersonatedUser?.id) {
    headers['X-Impersonate-User-Id'] = state.impersonatedUser.id;
  }
  return headers;
};

const organizationImportService = {
  /**
   * Validate a YAML/JSON import file. Returns a structured report.
   * Throws only on transport/HTTP errors; schema problems come back as
   * `errors` inside the report.
   */
  validate: async (file: File): Promise<ImportValidationReport> => {
    const formData = new FormData();
    formData.append('file', file);
    const response = await apiClient.post<ImportValidationReport>(
      `${apiBaseURL}/validate`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' } },
    );
    return response.data;
  },

  /**
   * Run the import. Reads a Server-Sent Events stream from the server,
   * forwarding each `{event, data}` to `onEvent`. Resolves with the new
   * organization id on `complete`, rejects on `error` or HTTP failure.
   *
   * Uses native fetch (not axios) because we need the response body as a
   * stream and a custom Authorization header. Aborts cleanly via `signal`.
   */
  commit: async (
    file: File,
    onEvent: (evt: ImportEvent) => void,
    signal?: AbortSignal,
  ): Promise<CommitResult> => {
    const formData = new FormData();
    formData.append('file', file);

    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}${apiBaseURL}/commit`, {
      method: 'POST',
      headers: buildAuthHeaders(),
      body: formData,
      signal,
    });

    if (!response.ok) {
      const text = await response.text().catch(() => '');
      throw new Error(text || `HTTP ${response.status}`);
    }

    const reader = response.body?.getReader();
    if (!reader) throw new Error('No response body');

    const decoder = new TextDecoder();
    let buffer = '';
    let result: CommitResult | null = null;
    type FinalError = { message: string; data: Record<string, unknown> };
    let finalError: FinalError | null = null;

    const flushBlock = (block: string) => {
      let eventName: ImportEventName = 'progress';
      const dataLines: string[] = [];
      for (const line of block.split('\n')) {
        if (line.startsWith('event: ')) eventName = line.slice(7).trim() as ImportEventName;
        else if (line.startsWith('data: ')) dataLines.push(line.slice(6));
      }
      if (!dataLines.length) return;
      let data: Record<string, unknown> = {};
      try {
        data = JSON.parse(dataLines.join('\n'));
      } catch {
        return;
      }
      onEvent({ event: eventName, data });
      if (eventName === 'complete') {
        result = { organizationId: String(data.organization_id ?? '') };
      } else if (eventName === 'error') {
        finalError = {
          message: String(data.message ?? i18n.t('errors.orgImport.failed')),
          data,
        };
      }
    };

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      let idx = buffer.indexOf('\n\n');
      while (idx !== -1) {
        const block = buffer.slice(0, idx);
        buffer = buffer.slice(idx + 2);
        if (block.trim()) flushBlock(block);
        idx = buffer.indexOf('\n\n');
      }
    }
    if (buffer.trim()) flushBlock(buffer);

    const captured = finalError as FinalError | null;
    if (captured) {
      const err = new Error(captured.message);
      (err as Error & { data?: unknown }).data = captured.data;
      throw err;
    }
    if (!result) throw new Error(i18n.t('errors.orgImport.streamEnded'));
    return result;
  },

  /** URL for downloading one of the bundled example files. */
  exampleUrl: (fmt: 'yaml' | 'json', kind: 'full' | 'minimal'): string => {
    const baseUrl = getApiBaseUrl();
    return `${baseUrl}${apiBaseURL}/examples/${fmt}/${kind}`;
  },

  /**
   * Download an example file via authenticated fetch (the endpoint requires
   * super-admin auth, so a plain anchor `href` would fail).
   */
  downloadExample: async (fmt: 'yaml' | 'json', kind: 'full' | 'minimal'): Promise<void> => {
    const baseUrl = getApiBaseUrl();
    const response = await fetch(`${baseUrl}${apiBaseURL}/examples/${fmt}/${kind}`, {
      method: 'GET',
      headers: buildAuthHeaders(),
    });
    if (!response.ok) {
      throw new Error(i18n.t('errors.orgImport.downloadExample', { status: response.status }));
    }
    const blob = await response.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `organization_${kind}.${fmt === 'yaml' ? 'yaml' : 'json'}`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  },
};

export default organizationImportService;
